import { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { auditService } from '../../services/api';
import { Button, Badge, Skeleton, EmptyState, Toast } from '../../components/ui';
import { thStyle, tdStyle, iconBtnStyle } from '../../utils/tableStyles';

const ACTION_COLORS = {
  CREATE: '#10B981', UPDATE: '#3B82F6', DELETE: '#EF4444',
  LOGIN: '#8B5CF6', LOGOUT: '#F59E0B', ASSIGN: '#06B6D4', STATUS_CHANGE: '#EC4899',
};

const AuditLogs = () => {
  const { dark } = useTheme();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const PAGE_SIZE = 25;

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await auditService.getAll({
        page, limit: PAGE_SIZE,
        search: search || undefined,
        action: filterAction || undefined,
        module: filterModule || undefined,
      });
      setLogs(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      setToast({ msg: 'Error loading audit logs', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, search, filterAction, filterModule]);

  useEffect(() => { fetch(); }, [fetch]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const card = { background: dark ? '#0F172A' : '#fff', border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`, borderRadius: 16, overflow: 'hidden' };

  const MODULES = ['leads', 'users', 'roles', 'courses', 'auth', 'csv', 'followups'];
  const ACTIONS = Object.keys(ACTION_COLORS);

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>Audit Logs</h1>
        <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: '4px 0 0' }}>Complete audit trail of all system actions</p>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', gap: 10, padding: '14px 20px', borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}`, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} color={dark ? '#475569' : '#94A3B8'} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by user, description..." style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, fontSize: 13, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, background: dark ? '#1E293B' : '#F8FAFC', color: dark ? '#F1F5F9' : '#0F172A', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }} style={{ padding: '7px 10px', borderRadius: 8, fontSize: 13, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, background: dark ? '#1E293B' : '#F8FAFC', color: dark ? '#F1F5F9' : '#0F172A', outline: 'none' }}>
            <option value="">All Actions</option>
            {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filterModule} onChange={e => { setFilterModule(e.target.value); setPage(1); }} style={{ padding: '7px 10px', borderRadius: 8, fontSize: 13, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, background: dark ? '#1E293B' : '#F8FAFC', color: dark ? '#F1F5F9' : '#0F172A', outline: 'none' }}>
            <option value="">All Modules</option>
            {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <button onClick={() => { setSearch(''); setFilterAction(''); setFilterModule(''); setPage(1); }} style={iconBtnStyle(dark)}><RefreshCw size={14} /></button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20 }}><Skeleton rows={8} cols={5} /></div>
          ) : logs.length === 0 ? (
            <div style={{ padding: 40 }}><EmptyState title="No audit logs found" /></div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                <tr>
                  <th style={thStyle(dark)}>Timestamp</th>
                  <th style={thStyle(dark)}>User</th>
                  <th style={thStyle(dark)}>Action</th>
                  <th style={thStyle(dark)}>Module</th>
                  <th style={thStyle(dark)}>Description</th>
                  <th style={thStyle(dark)}>IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                    <td style={{ ...tdStyle(dark), fontSize: 11, whiteSpace: 'nowrap' }}>
                      <div>{new Date(log.createdAt).toLocaleDateString()}</div>
                      <div style={{ color: dark ? '#64748B' : '#94A3B8' }}>{new Date(log.createdAt).toLocaleTimeString()}</div>
                    </td>
                    <td style={tdStyle(dark)}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{log.user ? `${log.user.firstName} ${log.user.lastName || ''}` : 'System'}</div>
                      <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>{log.user?.role?.name || ''}</div>
                    </td>
                    <td style={tdStyle(dark)}>
                      <Badge color={ACTION_COLORS[log.action] || '#6B7280'}>{log.action}</Badge>
                    </td>
                    <td style={{ ...tdStyle(dark), textTransform: 'capitalize' }}>{log.module || '—'}</td>
                    <td style={{ ...tdStyle(dark), maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.description || '—'}</td>
                    <td style={{ ...tdStyle(dark), fontSize: 11, fontFamily: 'monospace' }}>{log.ipAddress || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
            <span style={{ fontSize: 12, color: dark ? '#64748B' : '#94A3B8' }}>Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
